import { useState } from "react";
import { toastr } from "react-redux-toastr";
import { useIsMounted } from "../../../hooks/useIsMounted";
import { useSubmitTrafficSourceLogic } from "./submitTrafficSource.logic";
import { useFormTouched } from "../../../hooks/useFormTouched";
import { changeEmptyStringToNull } from "../../../helpers";
import { TrafficSource } from "../../../services/TrafficSource";
const initialFormValues = {
  name: "",
  details: "",
  status: true
};

const initialErrorValues = {
  name: false,
  details: false
};

export const useTrafficSourceFormLogic = (trafficSourceID, openTable) => {
  const [formValues, setFormValues] = useState(() => initialFormValues);
  const [errorValues, setErrorValues] = useState(() => initialErrorValues);

  const { isSubmitting, saveTrafficSource, updateTrafficSource } =
    useSubmitTrafficSourceLogic();

  const { formTouched, handleFormTouched, clearFormTouched } = useFormTouched({
    cleanupFunction: () => {}
  });

  const [editing, setIsEditing] = useState({
    id: "",
    isEditing: false
  });

  const [loadingTrafficSourceData, setLoadingTrafficSourceData] = useState({
    isLoading: false,
    error: false
  });

  const isMounted = useIsMounted();

  async function getTrafficSource(trafficSourceId) {
    try {
      setLoadingTrafficSourceData({ isLoading: true, error: false });
      const trafficSource = await TrafficSource.fetchTrafficSource(
        trafficSourceId
      );
      if (trafficSource.success && isMounted()) {
        setLoadingTrafficSourceData({ isLoading: false, error: false });
        setFormValues({
          name: trafficSource.data.trafficSource.name || "",
          details: trafficSource.data.trafficSource.details || "",
          status: trafficSource.data.trafficSource.status || true
        });
        setIsEditing({
          id: trafficSource.data.trafficSource.id,
          isEditing: true
        });
      } else {
        throw new Error(trafficSource.error.message);
      }
    } catch (error) {
      if (isMounted()) {
        setLoadingTrafficSourceData({ isLoading: false, error: true });
        console.trace(error.message);
        toastr.warning("Oops!", error.message);
      }
    }
  }

  const handleFormValueChange = (e) => {
    handleFormTouched();
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value
    });
    setErrorValues({
      ...errorValues,
      [name]: false
    });
  };

  const handleStatusChange = () => {
    handleFormTouched();
    setFormValues({
      ...formValues,
      status: !formValues.status
    });
  };

  const handleAddTrafficSource = async (dataToSend) => {
    const savedTrafficSource = await saveTrafficSource(dataToSend);
    if (savedTrafficSource.success) {
      toastr.success("Success!", savedTrafficSource.msg);
      setIsEditing({
        id: savedTrafficSource.data.id,
        isEditing: true
      });
      clearFormTouched();
      openTable();
    } else {
      toastr.error("Oops!", savedTrafficSource.msg);
    }
  };

  const handleUpdateTrafficSource = async (dataToSend) => {
    const updatedTrafficSource = await updateTrafficSource(
      editing.id,
      dataToSend
    );
    if (updatedTrafficSource.success) {
      toastr.success("Success!", updatedTrafficSource.msg);
      clearFormTouched();
      openTable();
    } else {
      toastr.error("Oops!", updatedTrafficSource.msg);
    }
  };

  const validateForm = () => {
    let isValid = true;
    if (!changeEmptyStringToNull(formValues.name)) {
      setErrorValues({
        ...errorValues,
        name: true
      });
      isValid = false;
    }
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    let dataToSend = {
      name: formValues.name.trim(),
      details: changeEmptyStringToNull(formValues.details),
      status: formValues.status
    };
    if (editing.isEditing) {
      handleUpdateTrafficSource(dataToSend);
    } else {
      handleAddTrafficSource(dataToSend);
    }
  };

  const handleCancel = () => {
    clearFormTouched();
    openTable();
  };

  useState(() => {
    if (trafficSourceID) {
      getTrafficSource(trafficSourceID);
    }
  }, [trafficSourceID]);

  return {
    formValues,
    errorValues,
    loadingTrafficSourceData,
    handleFormValueChange,
    handleStatusChange,
    handleSubmit,
    handleCancel,
    isSubmitting,
    editing,
    formTouched
  };
};
